import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { Feature, FeatureCheckResult } from '../../types/subscription';

/**
 * Service for checking feature access and managing feature-related API calls
 * @feature PLATFORM_API_ACCESS - Provides API access for feature checking and management
 */
export class FeatureAccessService extends BaseService {
  /**
   * Get user's active features
   */
  static async getUserFeatures(userId: string): Promise<Feature[]> {
    try {
      return await this.get<Feature[]>(API_ENDPOINTS.FEATURE_ACCESS.USER_FEATURES(userId));
    } catch (error) {
      console.error('[FeatureAccessService] Error fetching user features:', error);
      return [];
    }
  }

  /**
   * Check if user has access to a specific feature
   */
  static async checkFeature(
    userId: string,
    featureCode: string
  ): Promise<FeatureCheckResult> {
    try {
      return await this.get<FeatureCheckResult>(
        API_ENDPOINTS.FEATURE_ACCESS.CHECK_FEATURE(userId, featureCode)
      );
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.error('[FeatureAccessService] Error checking feature access:', error);
      }
      return {
        feature_code: featureCode,
        has_feature: false,
        source: null,
      };
    }
  }

  /**
   * Batch check multiple features for a user
   */
  static async checkFeatureBatch(
    userId: string,
    featureCodes: string[]
  ): Promise<Record<string, boolean>> {
    try {
      const checks = await Promise.all(
        featureCodes.map(code => this.checkFeature(userId, code))
      );

      return checks.reduce((acc, check) => {
        acc[check.feature_code] = check.has_feature;
        return acc;
      }, {} as Record<string, boolean>);
    } catch (error) {
      console.error('[FeatureAccessService] Error in batch feature check:', error);
      // Return all false if error
      return featureCodes.reduce((acc, code) => {
        acc[code] = false;
        return acc;
      }, {} as Record<string, boolean>);
    }
  }
}

